import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {
  APIService,
  CreateCourseResourceInput,
  elStatus,
  ModelCourseResourceFilterInput, ModelSortDirection, ResourceType, UpdateCourseResourceInput,
} from '../../../API.service';
import {getDeepFromObject} from '../../auth/helpers';
import {COURSES_MANAGER_OPTIONS} from './courses.options';
import * as _ from 'lodash';
import {Logger} from '@aws-amplify/core';
import {SyncSettings} from '../../../../@fuse/services/sync-settings.service';
import {FuseProgressBarService} from '../../../../@fuse/components/progress-bar/progress-bar.service';
import {CourseCat} from './category.model';
import * as S3 from 'aws-sdk/clients/s3';
import {CResource} from './resource.model';
import {API} from 'aws-amplify';

type ModelPartial<T> = {
  [P in keyof  T]?: T[P]
};

@Injectable()
export class CoursesResourcesService {
  private logger = new Logger('Category List');

  categories: CourseCat[];
  category: CourseCat = new CourseCat();

  currentCourseId: string;
  resource: CResource = new CResource();
  resources: CResource[];
  pageSize: number;
  nextToken: string = null;
  bucket: any;
  bucketTarget: any;

  private resourcesOnChanged = new BehaviorSubject<CResource[]>([]);
  resourcesOnChanged$ = this.resourcesOnChanged.asObservable();

  private onResourceChanged: BehaviorSubject<CResource> = new BehaviorSubject(this.resource);
  public onResourceChanged$: Observable<CResource> = this.onResourceChanged.asObservable();

  private nextPageToken: BehaviorSubject<any> = new BehaviorSubject(this.nextToken);
  public nextPageToken$: Observable<any> = this.nextPageToken.asObservable();

  private _onErrorOrSuccess = new BehaviorSubject<any>(null);
  onErrorOrSuccess$ = this._onErrorOrSuccess.asObservable();

  /**
   * Constructor
   *
   * @param options
   * @param _api
   * @param syncSettings
   * @param _fuseProgressBarService
   */
  constructor(
      @Inject(COURSES_MANAGER_OPTIONS) protected options = {},
      private _api: APIService,
      private syncSettings: SyncSettings,
      private _fuseProgressBarService: FuseProgressBarService
  )
  {
    this.bucketTarget = this.getConfigValue('S3BucketName');
    this.pageSize = this.getConfigValue('pageSize');
    this.syncSettings.onSettingsChanged$.subscribe((res) => {
      this.pageSize = res.pageSize;
      this._api.OnCreateCourseResourceListener
        .subscribe(resCreate => {
          if (this.currentCourseId) {
            this.refresh(this.currentCourseId);
          }
        });
      this._api.OnDeleteCourseResourceListener
        .subscribe(resDel => {
          if (this.currentCourseId) {
            this.refresh(this.currentCourseId);
          }
        });
      this._api.OnUpdateCourseResourceListener
        .subscribe(resDel => {
          if (this.currentCourseId) {
            this.refresh(this.currentCourseId);
          }
        });
    });
  }

  /**
   * Resolver
   *
   * @returns {Observable<any> | Promise<any> | any}
   * @param courseId
   */

  public refresh(courseId?: string) {
      this.currentCourseId = courseId;
      of(this.getResources(this.currentCourseId));
  }

  public goNextPage(courseId: string, filterTxt: string, nextPageToken: string) {
    of(this.getResources(courseId, filterTxt,  nextPageToken));
  }

  public sendMsg(msg: any) {
    this._onErrorOrSuccess.next(msg);
  }


  getResources(courseId?: string, searchTxt?: string, nextToken?: string): Promise<any> {
      let filter: ModelCourseResourceFilterInput = {
          courseId: { eq: courseId}
      };
      if (searchTxt !== undefined && searchTxt.length > 0) {
          filter = {
              description: { contains: searchTxt}
          };
      }
      this.nextToken = nextToken ? nextToken : null;
      this._fuseProgressBarService.show();
      return new Promise((resolve, reject) => {
          this._api.ListCourseResources(
            filter,
            this.pageSize,
            this.nextToken
          ).then((result) => {
              this.nextToken = !_.isEmpty(result['nextToken']) ? result['nextToken'] : null;
              this.nextPageToken.next(this.nextToken );
              this.resources = result.items;
              this.resourcesOnChanged.next(result.items);
              this._fuseProgressBarService.hide();
              resolve(this.resources);
          }).catch((err) => {
            this.catchError(err);
            reject(err);
          });
      });
  }

  create(fileUrl: string , courseId: string, file: File ): Promise<any>  {
    this._fuseProgressBarService.show();
    return new Promise(async (resolve, reject) => {
      const sizeStr = file.size.toString(11);
      const payload: CreateCourseResourceInput = {
        courseId: courseId,
        description: file.name,
        contentType: ResourceType.File,
        sourceCode: fileUrl,
        fileName: file.name,
        fileType: file.type,
        fileSize: sizeStr,
        status: elStatus.active,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        _version: null
      };
      this._api.CreateCourseResource(payload)
        .then(privacySetts => {
          this.resource = this.mergeObjs(this.resource, privacySetts);
          this.onResourceChanged.next(this.resource);
          this._onErrorOrSuccess.next({type: 'SUCCESS'});
          this._fuseProgressBarService.hide();
          resolve(this.resource);
        }).catch(error => {
          console.log(error);
          this._onErrorOrSuccess.next({type: 'error', msg: error.message});
          this.catchError({type: 'error', msg: error.message});
          reject(error);
      });
    });
  }

  updateFileResource(data: any, courseId: string): Promise<any> {
    this._fuseProgressBarService.show();
    data.updatedAt = new Date().toISOString();
    return new Promise((resolve, reject) => {
      const payload: UpdateCourseResourceInput = {
        id: data.id,
        courseId: courseId,
        description: data.description,
        contentType: ResourceType.File,
        status: elStatus.active,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        _version: data._version
      };
      this._api.UpdateCourseResource(payload)
        .then(resSetts => {
          this._fuseProgressBarService.hide();
          this._onErrorOrSuccess.next({type: 'SUCCESS'});
          resolve(resSetts);
        }).catch( error => {
        this.catchError({type: 'error', msg: error});
        reject(error);
      });
    });
  }

  createOther(data: any , courseId: string ): Promise<any>  {
    this._fuseProgressBarService.show();
    return new Promise(async (resolve, reject) => {
      const payload: CreateCourseResourceInput = {
        courseId: courseId,
        description: data.description,
        contentType: ResourceType.Link,
        sourceCode: data.sourceCode,
        status: elStatus.active,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        _version: null
      };
      this._api.CreateCourseResource(payload)
        .then(otherSetts => {
          this._onErrorOrSuccess.next({type: 'SUCCESS'});
          this._fuseProgressBarService.hide();
          resolve(otherSetts);
        }).catch(error => {
          this._onErrorOrSuccess.next({type: 'error', msg: error.message});
          this.catchError({type: 'error', msg: error.message});
          reject(error);
      });
    });
  }

  updateOther(data: any, courseId: string): Promise<any> {
    this._fuseProgressBarService.show();
    data.updatedAt = new Date().toISOString();
    return new Promise((resolve, reject) => {
      const payload: UpdateCourseResourceInput = {
        id: data.id,
        courseId: courseId,
        description: data.description,
        contentType: ResourceType.Link,
        sourceCode: data.sourceCode,
        status: elStatus.active,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        _version: data._version
      };
      this._api.UpdateCourseResource(payload)
        .then(resSetts => {
          this._fuseProgressBarService.hide();
          this._onErrorOrSuccess.next({type: 'SUCCESS'});
          resolve(resSetts);
        }).catch( error => {
          this.catchError({type: 'error', msg: error});
          reject(error);
      });
    });
  }

  takeScreenShot(linkUrl: string): Promise<any> {
    this._fuseProgressBarService.show();
    return new Promise((resolve, reject) => {
      const init = {
        queryStringParameters: {
          url: linkUrl
        },
      };
      API.get('api3575e6f1', '/screenshot', init)
        .then(result => {
          this._fuseProgressBarService.hide();
          resolve(result);
        }).catch((error: any) => {
          this.catchError({type: 'error', msg: error});
          reject(error);
      });
    });
  }

  countResourcesByCourseIdAndName(courseId: string, fileName: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let filter: ModelCourseResourceFilterInput ;
      if (fileName !== undefined && fileName) {
        filter = {
          fileName: { eq: fileName}
        };
      }
      this._api.ListResourcesByCourseId(
        courseId,
        ModelSortDirection.DESC,
        filter,
        10
      ).then((result) => {
        resolve(result.items.length);
      }).catch((err) => {
        this.catchError(err);
        reject(err);
      });
    });
  }

  // deleteCategory(propsIn: any): Promise<any> {
  //   propsIn.updatedAt = new Date().toISOString();
  //   this._fuseProgressBarService.show();
  //   return new Promise((resolve, reject) => {
  //       const payloadUpdate: UpdateCourseCategoryInput = {
  //           id: propsIn.id,
  //           _version: propsIn._version,
  //           status: elStatus.inactive,
  //           updatedAt: propsIn.updatedAt
  //       };
  //       this._api.UpdateCourseCategory(payloadUpdate)
  //         .then(updateSetts => {
  //           const payload: DeleteCourseCategoryInput = {
  //             id: updateSetts.id,
  //             _version: updateSetts._version
  //           };
  //           return this._api.DeleteCourseCategory(payload);
  //       }).then(delSetts => {
  //         this._fuseProgressBarService.hide();
  //         this._onErrorOrSuccess.next({type: 'SUCCESS'});
  //         resolve(delSetts);
  //       }).catch( error => {
  //         this.catchError({type: 'error', msg: error});
  //         reject(error);
  //     });
  //   });
  // }

  syncS3(): any {
    return new S3(
      {
        accessKeyId: this.getConfigValue('accessKeyId'),
        secretAccessKey: this.getConfigValue('secretAccessKey'),
        region: this.getConfigValue('region')
      });
  }

  uploadFile(fileUpload: File, courseId: string): Promise<any>  {
    return new Promise((resolve, reject) => {
      const bPath = `${this.bucketTarget}/course/${courseId}`;
      const params = {
        Bucket: bPath,
        Key: fileUpload.name,
        ACL: 'authenticated-read',
        Body: fileUpload,
        ContentType: fileUpload.type
      };
      const self = this;
      this._fuseProgressBarService.setMode('determinate');
      this._fuseProgressBarService.setValue(0);
      this._fuseProgressBarService.show();
      const newConn = this.syncS3();
      newConn.upload(params)
        .on('httpUploadProgress', (evt) => {
          const perUp = Math.round((evt.loaded * 100) / evt.total);
          self._fuseProgressBarService.setValue(perUp);
        }).send((err, data) => {
          self._fuseProgressBarService.hide();
          self._fuseProgressBarService.setValue(0);
          if (err) {
            reject(err);
          } else {
            resolve(data.Location);
          }
      });
    });
  }

  getPresignedFile(fileName: string, courseId: string): Promise<any> {
    const bPath = `${this.bucketTarget}/course/${courseId}`;
    const newConn = this.syncS3();
    const params = {
      Bucket: bPath,
      Key: fileName,
      Expires: 3600
    };
    return new Promise((resolve, reject) => {
      newConn.getSignedUrl( 'getObject', params, (err, urlLink) => {
        if (err) {
          console.log('There was an error Downloading your file: ', err);
          reject(err);
        } else {
          resolve(urlLink);
        }
      });
    });
  }

  private catchError(error) {
    console.log(error);
    this.logger.debug('OOPS!', error);
    this._fuseProgressBarService.hide();
  }

  getConfigValue(key: string): any {
      return getDeepFromObject(this.options, key, null);
  }

  cleanObj(objB: any): any {
    const cleanArray = ['__typename', '_deleted', '_lastChangedAt', 'courses'];
    const newObj = {};
    for (const key in objB) {
      if (cleanArray.indexOf(key) === -1){
        newObj[key] = objB[key];
      }
    }
    return newObj;
  }

  mergeObjs(objA: any, objB: ModelPartial<any>) {
    return {...objA, ...objB};
  }
}
